import { BF001 } from "../../domain/buyerFundingFlowSmoke";
import { dollars } from "../../domain/money";
import { BF001_MASKED_PHONE } from "./deviceVerification";
import {
  BUYER_FUNDING_FLOW_MANUAL_SOURCE_LIMITATION,
  type BuyerFundingFlowPlaidEvidence,
  type BuyerFundingFlowSession
} from "./types";

/** Published buyer funding files the flow can load after lookup. */
export function getBuyerFundingFlowSession(fileCode: string): BuyerFundingFlowSession | undefined {
  const normalized = fileCode.trim().toUpperCase();
  if (normalized === BF001.fileCode) {
    return {
      fileCode: BF001.fileCode,
      officeName: BF001.office,
      buyerName: BF001.buyerName,
      buyerEmail: BF001.buyerEmail,
      buyerLastName: BF001.buyerLastName,
      propertyAddress: BF001.property,
      amountDue: BF001.amountDue,
      amountDueLookup: BF001.amountDueLookup,
      closingDate: "2026-06-07",
      invitePhoneMasked: BF001_MASKED_PHONE,
      paymentRequest: {
        requestId: "pay-req-bf-001",
        purpose: "Cash to close",
        requestedAt: BF001.officePublishedAt,
        requestedBy: BF001.office,
        officeCoverageNote:
          "Ask your office about any transfer insurance or good-funds programs. Veto does not provide insurance on payments."
      },
      instructions: {
        versionLabel: BF001.instructionVersion,
        publishedAt: BF001.officePublishedAt,
        paymentRail: "Wire",
        deadlineAt: "2026-06-06T21:00:00.000Z",
        beneficiaryName: BF001.beneficiary,
        creditTo: BF001.beneficiary,
        bankName: BF001.bank,
        routingLast4: BF001.routingLast4,
        accountLast4: BF001.destinationAccountLast4,
        furtherCreditTo: "Sample Escrow Trust Account · Acct ending 7316",
        correspondentBank: "Not applicable for this wire",
        memoRequired: BF001.fileCode,
        officeContactNote:
          "Use only these instructions. If you receive different wire details by email or text, stop and contact the office through an approved channel."
      }
    };
  }
  return undefined;
}

export function buyerFundingFlowLookupMatches(
  session: BuyerFundingFlowSession,
  fileCode: string,
  buyerLastName: string,
  amountDue: string
): boolean {
  const parsed = Number.parseFloat(amountDue.replace(/[^0-9.]/g, ""));
  const normalizedAmount = Number.isFinite(parsed) ? parsed.toFixed(2) : "";
  return (
    fileCode.trim().toUpperCase() === session.fileCode &&
    buyerLastName.trim().toLowerCase() === session.buyerLastName.toLowerCase() &&
    normalizedAmount === session.amountDueLookup
  );
}

export function simulatePlaidConnect(session: BuyerFundingFlowSession): BuyerFundingFlowSession {
  return {
    ...session,
    plaid: {
      mode: "simulated",
      connectedAt: new Date().toISOString(),
      ownerName: session.buyerName,
      institutionName: "Sample Source Bank",
      accountLast4: "8844",
      limitation:
        "Simulated Plaid connect for development. Live Plaid Link activates when VITE_PLAID_LINK_TOKEN is configured."
    }
  };
}

export function skipPlaid(session: BuyerFundingFlowSession): BuyerFundingFlowSession {
  return {
    ...session,
    plaid: {
      mode: "skipped",
      connectedAt: new Date().toISOString(),
      limitation: "Buyer continued without bank connection. Office may request source evidence separately."
    }
  };
}

export function recordManualSourceAccount(
  session: BuyerFundingFlowSession,
  input: {
    institutionName: string;
    accountLast4: string;
    routingLast4?: string;
    ownerName?: string;
  }
): BuyerFundingFlowSession {
  const evidence: BuyerFundingFlowPlaidEvidence = {
    mode: "manual",
    connectedAt: new Date().toISOString(),
    ownerName: input.ownerName?.trim() || session.buyerName,
    institutionName: input.institutionName.trim(),
    accountLast4: input.accountLast4.replace(/\D/g, "").slice(-4),
    routingLast4: input.routingLast4?.replace(/\D/g, "").slice(-4),
    limitation: BUYER_FUNDING_FLOW_MANUAL_SOURCE_LIMITATION
  };
  return { ...session, plaid: evidence };
}

export function dollarsFromLookup(amountDue: string): ReturnType<typeof dollars> {
  const parsed = Number.parseFloat(amountDue.replace(/[^0-9.]/g, ""));
  return dollars(Number.isFinite(parsed) ? parsed : 0);
}
